/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { Link } from "react-router-dom";
import PolicyLanguageToggle, {
  usePolicyLanguage,
} from "../components/PolicyLanguageToggle";

const copy = {
  es: {
    title: "Normas de la comunidad",
    date: "Vigentes desde el 30 de julio de 2026 · Versión 2026-07-30",
    introduction:
      "Estas Normas explican los estándares aplicables a observaciones, fotografías, perfiles, conversaciones en foros, comentarios, reacciones y otras actividades comunitarias en BioIndex™.",
    termsPrefix: "Forman parte de las",
    termsLabel: "Condiciones de uso",
    contact: "Contactar a BioIndex",
    returnHome: "Volver a BioIndex",
    rights: "Todos los derechos reservados.",
    sections: [
      {
        title: "Participa con respeto",
        text: "Trata a los demás usuarios con respeto, incluso cuando no estén de acuerdo sobre una identificación, interpretación científica, fotografía o decisión de moderación. Se prohíben el acoso, las amenazas, la intimidación, la humillación dirigida, las conductas de odio, los ataques discriminatorios y alentar a otras personas a atacar a un usuario.",
      },
      {
        title: "Comparte registros genuinos de biodiversidad",
        text: "Envía observaciones y fotografías de buena fe. Los errores y las identificaciones inciertas son esperables y pueden discutirse de manera constructiva. Se prohíben las observaciones fabricadas deliberadamente, las pruebas manipuladas, las ubicaciones o fechas que se saben falsas, las identificaciones engañosas reiteradas y los intentos por presentar el trabajo de otra persona como propio.",
      },
      {
        title: "Protege la vida silvestre y los lugares sensibles",
        text: "No molestes, captures, manipules, lastimes, cebes, recolectes ni traslades organismos únicamente para crear contenido. Cumple los permisos, las reglas de áreas protegidas, las normas de bienestar animal y las restricciones de acceso aplicables. Evita publicar ubicaciones exactas de especies amenazadas, nidos, madrigueras, dormideros, sitios reproductivos, propiedades privadas o lugares cultural o ecológicamente sensibles cuando su divulgación pueda crear un riesgo.",
      },
      {
        title: "Mantén el contenido apropiado",
        text: "No envíes contenido ilegal, amenazante, que promueva el odio, acosador, sexualmente explícito, explotador, fraudulento, difamatorio o intencionalmente perturbador. Se prohíbe el contenido que promueva daño, tráfico, caza furtiva, delitos contra la vida silvestre, interferencia peligrosa con animales u otras actividades ilegales. El material científico o educativo debe presentarse responsablemente y con el contexto adecuado.",
      },
      {
        title: "Respeta la privacidad y la identidad",
        text: "No publiques direcciones privadas, ubicaciones personales precisas, comunicaciones confidenciales, documentos de identidad, datos de contacto u otra información sensible sin autorización. No suplantes a otra persona u organización, no crees perfiles engañosos ni utilices BioIndex para rastrear, exponer o intimidar a otra persona.",
      },
      {
        title: "Respeta la propiedad intelectual",
        text: "Carga únicamente fotografías, escritos y otros materiales creados por ti o que tengas autorización para utilizar. Da el crédito correspondiente cuando sea necesario. No elimines atribuciones, copies la observación de otro usuario ni envíes material protegido por derechos de autor de una manera que vulnere los derechos de su propietario.",
      },
      {
        title: "Evita el spam y la manipulación",
        text: "No publiques promociones repetitivas, anuncios irrelevantes, estafas, enlaces maliciosos, contenido automatizado ni solicitudes masivas. No manipules votos, reacciones, kudos, amistades, retos, títulos, clasificaciones, denuncias u otros sistemas comunitarios mediante cuentas duplicadas, abuso coordinado o actividad automatizada.",
      },
      {
        title: "Participa constructivamente en las conversaciones",
        text: "Mantén los temas y comentarios del foro relacionados con BioIndex, biodiversidad, fotografía, taxonomía, comunicación científica, conservación o el propósito declarado de la conversación. Corrige ideas e identificaciones con evidencia y una explicación constructiva. Se prohíben los ataques personales y la interrupción reiterada de las conversaciones.",
      },
      {
        title: "Denuncia los problemas",
        text: (
          <>
            Cuando haya un control de denuncia disponible, utilízalo para
            reportar el contenido o la cuenta correspondiente. También puedes
            enviar denuncias mediante la{" "}
            <Link
              to="/contact"
              className="font-medium text-[#01767A] underline"
            >
              página de contacto de BioIndex
            </Link>
            . Identifica el contenido, la cuenta o la conversación involucrada y
            proporciona una explicación breve. No utilices indebidamente las
            herramientas de denuncia para tomar represalias ni silenciar
            desacuerdos expresados de buena fe.
          </>
        ),
      },
      {
        title: "Bloqueos y límites personales",
        text: "Utiliza los controles de bloqueo disponibles cuando no quieras que otro usuario interactúe contigo o aparezca en tu experiencia comunitaria. No evadas un bloqueo mediante otra cuenta ni pidas a otros usuarios que contacten a alguien en tu nombre. El bloqueo no impide que BioIndex conserve la información necesaria para investigar una denuncia.",
      },
      {
        title: "Moderación y aplicación",
        text: "BioIndex puede revisar denuncias y la actividad pertinente de una cuenta. Según las circunstancias, BioIndex puede emitir una advertencia, eliminar contenido, limitar su visibilidad, restringir una función, conservar pruebas, suspender una cuenta o cerrarla. Las conductas graves pueden dar lugar a medidas inmediatas. Las infracciones reiteradas y los intentos por evadir las medidas pueden generar restricciones más fuertes.",
      },
      {
        title: "Apelaciones y correcciones",
        text: (
          <>
            Si crees que una medida de moderación se tomó por error, puedes
            solicitar una revisión mediante la{" "}
            <Link
              to="/contact"
              className="font-medium text-[#01767A] underline"
            >
              página de contacto de BioIndex
            </Link>
            . Explica la medida que deseas apelar y proporciona el contexto
            pertinente. BioIndex puede restaurar el contenido o el acceso cuando
            la revisión muestre que la decisión original fue equivocada o ya no
            es necesaria.
          </>
        ),
      },
    ],
  },
  en: {
    title: "Community Guidelines",
    date: "Effective July 30, 2026 · Version 2026-07-30",
    introduction:
      "These Guidelines explain the standards for observations, photographs, profiles, forum discussions, comments, reactions, and other community activity on BioIndex™.",
    termsPrefix: "They form part of the",
    termsLabel: "Terms of Use",
    contact: "Contact BioIndex",
    returnHome: "Return to BioIndex",
    rights: "All rights reserved.",
    sections: [
      {
        title: "Participate respectfully",
        text: "Treat other users with respect, including when you disagree about an identification, scientific interpretation, photograph, or moderation decision. Harassment, threats, intimidation, targeted humiliation, hateful conduct, discriminatory attacks, or encouraging others to target a user are prohibited.",
      },
      {
        title: "Share genuine biodiversity records",
        text: "Submit observations and photographs in good faith. Mistakes and uncertain identifications are expected and may be discussed constructively. Deliberately fabricated observations, manipulated evidence, knowingly false locations or dates, repeated deceptive identifications, and attempts to misrepresent another person's work as your own are prohibited.",
      },
      {
        title: "Protect wildlife and sensitive locations",
        text: "Do not disturb, capture, handle, injure, bait, collect, or relocate organisms solely to create content. Follow applicable permits, protected-area rules, animal-welfare standards, and access restrictions. Avoid publishing exact locations of threatened species, nests, dens, roosts, breeding sites, private properties, or culturally or ecologically sensitive places when disclosure could create risk.",
      },
      {
        title: "Keep content appropriate",
        text: "Do not submit illegal, threatening, hateful, harassing, sexually explicit, exploitative, fraudulent, defamatory, or intentionally disturbing content. Content promoting harm, trafficking, poaching, wildlife crime, dangerous interference with animals, or other illegal activity is prohibited. Scientific or educational material must still be presented responsibly and with appropriate context.",
      },
      {
        title: "Respect privacy and identity",
        text: "Do not publish private addresses, precise personal locations, confidential communications, identification documents, contact details, or other sensitive information without permission. Do not impersonate another person or organization, create deceptive profiles, or use BioIndex to track, expose, or intimidate another person.",
      },
      {
        title: "Respect intellectual property",
        text: "Upload only photographs, writing, and other material that you created or have permission to use. Give appropriate credit when required. Do not remove attribution, copy another user's observation, or submit copyrighted material in a way that violates the owner's rights.",
      },
      {
        title: "Avoid spam and manipulation",
        text: "Do not post repetitive promotions, irrelevant advertisements, scams, malicious links, automated content, or mass solicitations. Do not manipulate votes, reactions, kudos, friendships, challenges, titles, rankings, reports, or other community systems through duplicate accounts, coordinated abuse, or automated activity.",
      },
      {
        title: "Use discussions constructively",
        text: "Keep forum topics and comments relevant to BioIndex, biodiversity, photography, taxonomy, scientific communication, conservation, or the stated purpose of the discussion. Correct ideas and identifications with evidence and a constructive explanation. Personal attacks and repeated disruption of discussions are prohibited.",
      },
      {
        title: "Report problems",
        text: (
          <>
            When a reporting control is available, use it to report the relevant
            content or account. Reports may also be submitted through the{" "}
            <Link
              to="/contact"
              className="font-medium text-[#01767A] underline"
            >
              BioIndex contact page
            </Link>
            . Provide the content, account, or discussion involved and a concise
            explanation. Do not misuse reporting tools to retaliate against users
            or suppress good-faith disagreement.
          </>
        ),
      },
      {
        title: "Blocking and personal boundaries",
        text: "Use available blocking controls when you do not want another user to interact with you or appear in your community experience. Do not evade a block through another account or ask other users to contact someone on your behalf. Blocking does not prevent BioIndex from retaining information required to investigate a report.",
      },
      {
        title: "Moderation and enforcement",
        text: "BioIndex may review reports and relevant account activity. Depending on the circumstances, BioIndex may provide a warning, remove content, limit visibility, restrict a feature, preserve evidence, suspend an account, or terminate an account. Serious conduct may result in immediate action. Repeated violations and attempts to evade enforcement may lead to stronger restrictions.",
      },
      {
        title: "Appeals and corrections",
        text: (
          <>
            If you believe moderation action was taken in error, you may request a
            review through the{" "}
            <Link
              to="/contact"
              className="font-medium text-[#01767A] underline"
            >
              BioIndex contact page
            </Link>
            . Explain the action you are appealing and provide any relevant
            context. BioIndex may restore content or access when a review shows
            that the original action was mistaken or no longer necessary.
          </>
        ),
      },
    ],
  },
};

export default function CommunityGuidelinesPage() {
  const [language, setLanguage] = usePolicyLanguage();
  const content = copy[language];

  return (
    <main className="min-h-screen bg-[#374049] px-4 py-8 text-[#374049]">
      <section className="mx-auto max-w-5xl overflow-hidden rounded-3xl border border-[#93CA87]/40 bg-[#FCFFF6] shadow-lg">
        <div className="flex items-center justify-center bg-[#374049] px-6 py-6">
          <img
            src="/BioIndex Prototype Banner.png"
            alt="BioIndex"
            className="max-h-32 w-full max-w-md object-contain"
          />
        </div>

        <div className="px-6 py-8 sm:px-10 lg:px-14">
          <div className="flex justify-end">
            <PolicyLanguageToggle
              language={language}
              onChange={setLanguage}
            />
          </div>

          <div className="mt-6 max-w-3xl border-b border-[#374049]/10 pb-6">
            <h1
              className="text-4xl font-semibold"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              {content.title}
            </h1>

            <p className="mt-2 text-xs text-[#374049]/50">{content.date}</p>

            <p className="mt-5 text-sm leading-7 text-[#374049]/75">
              {content.introduction}
            </p>

            <p className="mt-3 text-sm leading-7 text-[#374049]/75">
              {content.termsPrefix}{" "}
              <Link
                to="/terms"
                className="font-medium text-[#01767A] underline"
              >
                {content.termsLabel}
              </Link>
              .
            </p>
          </div>

          <div className="mt-7 grid gap-4 md:grid-cols-2">
            {content.sections.map((section, index) => (
              <article
                key={section.title}
                className="rounded-2xl border border-[#374049]/10 bg-white/70 p-5 shadow-sm"
              >
                <div className="mb-3 flex items-center gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#24C3BE]/10 text-sm font-semibold text-[#24C3BE]">
                    {index + 1}
                  </span>

                  <h2 className="text-base font-semibold text-[#374049]">
                    {section.title}
                  </h2>
                </div>

                <div className="text-sm leading-7 text-[#374049]/70">
                  {section.text}
                </div>
              </article>
            ))}
          </div>

          <Link
            to="/contact"
            className="mt-7 inline-flex rounded-xl bg-[#24C3BE] px-5 py-2.5 text-sm font-semibold text-[#374049] transition hover:bg-[#1faeaa]"
          >
            {content.contact}
          </Link>

          <div className="mt-8 flex flex-col items-center border-t border-[#374049]/10 pt-7 text-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-xl bg-[#374049] px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#2f373e]"
            >
              {content.returnHome}
            </Link>

            <p className="mt-5 text-xs leading-6 text-[#374049]/55">
              © {new Date().getFullYear()} BioIndex™. {content.rights}
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
